import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { chatApi, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2, User } from 'lucide-react';

function formatTime(isoDate) {
  try {
    const date = new Date(isoDate);
    const now = new Date();
    const diffDays = Math.floor((now - date) / 86400000);
    if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export default function ChatPage() {
  const { user } = useAuth();
  const { markConversationRead, refreshUnreadCount } = useNotifications();
  const { conversationId } = useParams();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeConversation, setActiveConversation] = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const lastMsgCountRef = useRef(0);

  // Load conversation messages + start polling
  const loadMessages = useCallback(async (convId, isInitial = false) => {
    try {
      const { data } = await chatApi.getMessages(convId);
      setMessages(data);
      
      // Only scroll + mark read on initial load or when new messages arrive
      if (isInitial || data.length > lastMsgCountRef.current) {
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: isInitial ? 'auto' : 'smooth' }), 100);
        markConversationRead(convId);
      }
      lastMsgCountRef.current = data.length;
    } catch {
      // silently fail on poll
    }
  }, [markConversationRead]);

  useEffect(() => {
    if (!conversationId) {
      // No conversation ID - redirect to dashboard messages
      navigate('/dashboard', { state: { tab: 'messages' } });
      return;
    }

    // Fetch conversation details
    chatApi.getConversation(conversationId).then(({ data }) => {
      setActiveConversation(data);
      setLoading(false);
    }).catch(() => {
      setActiveConversation({ id: conversationId });
      setLoading(false);
    });

    // Initial load
    lastMsgCountRef.current = 0;
    loadMessages(conversationId, true);

    // Poll every 3 seconds for real-time feel
    pollRef.current = setInterval(() => loadMessages(conversationId), 3000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [conversationId, loadMessages, navigate]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId) return;

    const content = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistic update
    const tempMsg = {
      id: `temp-${Date.now()}`,
      sender_id: user.id,
      content,
      created_at: new Date().toISOString(),
      _sending: true,
    };
    setMessages(prev => [...prev, tempMsg]);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    try {
      await chatApi.sendMessage(conversationId, content);
      // Refresh immediately after send
      await loadMessages(conversationId);
      refreshUnreadCount();
    } catch (error) {
      toast.error(formatApiError(error));
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      setNewMessage(content);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Chat view
  const otherName = activeConversation?.other_party_name || activeConversation?.other_user_name || 'User';

  return (
    <div className="flex flex-col" style={{ height: 'calc(100dvh - 64px)' }} data-testid="chat-page">
      {/* Chat Header */}
      <div className="border-b border-border px-4 py-3 flex items-center gap-3 bg-background">
        <button
          onClick={() => navigate('/dashboard', { state: { tab: 'messages' } })}
          className="p-2 -ml-2 rounded-lg hover:bg-muted transition-colors"
          data-testid="chat-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <User className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="font-medium truncate">{otherName}</p>
          {activeConversation?.listing_title && (
            <p className="text-xs text-muted-foreground truncate">{activeConversation.listing_title}</p>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((msg) => {
          const isOwn = msg.sender_id === user?.id;
          return (
            <div
              key={msg.id}
              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${msg.id}`}
            >
              <div
                className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                  isOwn
                    ? 'bg-primary text-primary-foreground rounded-br-md'
                    : 'bg-muted rounded-bl-md'
                } ${msg._sending ? 'opacity-60' : ''}`}
              >
                <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                <p className={`text-[10px] mt-1 ${isOwn ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                  {formatTime(msg.created_at)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t border-border p-4 bg-background" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}>
        <form onSubmit={handleSend} className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1"
            disabled={sending}
            data-testid="message-input"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!newMessage.trim() || sending}
            data-testid="send-message-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}
